import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { PageLayout, CONTENT_MAX_WIDTH, type CommandItem } from '@vitness/layouts';
import { useTheme } from '@vitness/theme';
import { Eye, EyeOff } from '@vitness/icons';
import { useMasked } from '../lib/useMasked';
import { PullToRefresh, PULL_TO_REFRESH_KEY } from '../components/PullToRefresh';
import { StatusBanner } from './StatusBanner';
import { NAV_ITEMS, DEFAULT_PINNED, ROUTE_TITLES } from './nav';
import { AccountChip } from './AccountChip';
import { SyncBadge } from './SyncBadge';
import { useStore } from '../lib/store';
import { api } from '../lib/api';

/**
 * Everything responsive comes from PageLayout — expanded sidebar ≥1024px, the
 * same rail in icon mode 768–1023, drawer + bottom nav below that. We only
 * supply nav items and slots.
 */
export function VudgetShell({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const settings = useStore(s => s.data?.settings);
  const sync = useStore(s => s.sync);

  const [pinned, setPinned] = useState<string[]>(DEFAULT_PINNED);

  // Off unless switched on in Settings, and only on the Dashboard even then —
  // see PullToRefresh for why it isn't offered everywhere.
  const pullToRefresh = settings?.[PULL_TO_REFRESH_KEY] === '1' && pathname === '/';

  // hydrate the user's pinned tabs once bootstrap lands
  useEffect(() => {
    const raw = settings?.mobile_pinned_ids;
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length) setPinned(parsed);
    } catch { /* keep defaults */ }
  }, [settings?.mobile_pinned_ids]);

  const onPinnedChange = useCallback((ids: string[]) => {
    setPinned(ids);
    api.putSettings({ mobile_pinned_ids: JSON.stringify(ids) }).catch(() => {});
  }, []);

  const commands: CommandItem[] = useMemo(
    () => NAV_ITEMS.map(n => ({
      id: n.id,
      label: n.label,
      icon: n.icon,
      onSelect: () => navigate(n.id),
    })) as CommandItem[],
    [navigate],
  );

  return (
    <PageLayout
      navItems={NAV_ITEMS}
      activeId={pathname}
      onNavigate={navigate}
      header={<BrandHeader />}
      collapsedHeader={<BrandChip />}
      sidebarFooter={<AccountChip />}
      topBarTitle={ROUTE_TITLES[pathname] ?? 'Vudget'}
      topBarActions={<><MaskToggle /><SyncBadge /></>}
      mobileTitle={ROUTE_TITLES[pathname] ?? 'Vudget'}
      mobileActions={<><MaskToggle /><SyncBadge compact /></>}
      mobilePinnedIds={pinned}
      onMobilePinnedChange={onPinnedChange}
      mobileEditable
      mobileMaxItems={5}
      search={{ commands, placeholder: 'Jump to…' }}
      hideSearchOnDesktop
      maxWidth={CONTENT_MAX_WIDTH}
    >
      <PullToRefresh enabled={pullToRefresh} onRefresh={sync}>
        <StatusBanner />
        {children}
      </PullToRefresh>
    </PageLayout>
  );
}

/** Hide-amounts toggle — one tap, everywhere, because finance apps get opened in public. */
function MaskToggle() {
  const { masked, toggle } = useMasked();
  return (
    <button onClick={toggle} className="vd-iconbtn" aria-pressed={masked}
      title={masked ? 'Show amounts' : 'Hide amounts'}
      aria-label={masked ? 'Show amounts' : 'Hide amounts'}>
      {masked ? <EyeOff size={16} /> : <Eye size={16} />}
    </button>
  );
}

function BrandHeader() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
      <BrandChip />
      <span style={{
        fontFamily: 'var(--vn-font-display)', fontWeight: 800,
        fontSize: 15, letterSpacing: '-0.02em',
      }}>
        Vudget
      </span>
      <span style={{
        fontFamily: 'var(--vn-font-mono)', fontSize: 8, color: 'var(--vn-text-muted)',
        border: '1px solid var(--vn-border-strong)', borderRadius: 3, padding: '1px 4px',
      }}>
        CSP
      </span>
    </div>
  );
}

/**
 * The accent comes from the theme context rather than `var(--vn-accent)`.
 * ThemeProvider does publish that variable now, but it does so in an effect —
 * reading the context means the mark is the right colour on the very first
 * paint instead of a frame late.
 */
function BrandChip() {
  const { accent } = useTheme();
  return (
    <div style={{
      width: 26, height: 26, borderRadius: 'var(--vn-radius-md)', flexShrink: 0,
      background: accent, color: '#000',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: 'var(--vn-font-display)', fontWeight: 800, fontSize: 13,
      lineHeight: 1,
    }}>
      V
    </div>
  );
}
